import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Avatar,
  Rating,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";

import { Strings } from "@app/constants";
import { RequestStatus } from "@app/constants";
import {
  clearTestimonialDetail,
  getTestimonialDetail,
} from "../slice";
import { selectTestimonialDetailState } from "../selector";
import { ITestimonial } from "../types";

interface TestimonialDetailModalProps {
  open: boolean;
  onClose: () => void;
  testimonialId: number | string | null;
  initialData?: ITestimonial | null;
}

const TestimonialDetailModal: React.FC<TestimonialDetailModalProps> = ({
  open,
  onClose,
  testimonialId,
  initialData,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const detailState = useSelector(selectTestimonialDetailState);
  const isLoading = detailState?.status === RequestStatus.RUNNING;
  const testimonial: ITestimonial | null =
    detailState?.data || initialData || null;

  useEffect(() => {
    if (open && testimonialId) {
      dispatch(getTestimonialDetail({ id: testimonialId }));
    }
    return () => {
      if (open) {
        dispatch(clearTestimonialDetail());
      }
    };
  }, [open, testimonialId, dispatch]);

  const reviewerName =
    testimonial?.reviewerName ||
    testimonial?.name ||
    testimonial?.author ||
    "Reviewer";

  const commentText =
    testimonial?.comment ||
    testimonial?.review ||
    testimonial?.message ||
    "No comment provided.";

  const ratingValue = Number(testimonial?.rating ?? 5);
  const isVerified = Boolean(testimonial?.verifiedBuyer);
  const tag = testimonial?.tag || "";

  const imageUrl =
    testimonial?.image?.url ||
    (typeof testimonial?.image === "string" ? testimonial?.image : null) ||
    testimonial?.avatar;

  const dateString =
    testimonial?.createdAt ||
    testimonial?.created_at ||
    testimonial?.date ||
    "";

  const formattedDate = dateString
    ? new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "N/A";

  const fontFamily = Strings.fontLanguage.workSans || "'Work Sans', sans-serif";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: "flex",
            }}
          >
            <Icon icon="solar:chat-round-like-linear" width={24} height={24} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily }}>
              Review Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: #{testimonial?.id || testimonialId || "N/A"}
            </Typography>
          </Box>
        </Stack>

        <IconButton onClick={onClose} size="small">
          <Icon icon="mdi:close" width={20} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {isLoading && !testimonial ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Reviewer Profile Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Avatar
                src={imageUrl}
                alt={reviewerName}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: theme.palette.primary.main,
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {reviewerName.charAt(0).toUpperCase()}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "text.primary" }}
                  >
                    {reviewerName}
                  </Typography>

                  <Chip
                    label={isVerified ? "Verified Buyer" : "Reviewer"}
                    size="small"
                    icon={
                      isVerified ? (
                        <Icon icon="solar:check-circle-bold" width={14} />
                      ) : undefined
                    }
                    sx={{
                      backgroundColor: isVerified
                        ? alpha(theme.palette.success.main, 0.1)
                        : alpha(theme.palette.grey[500], 0.1),
                      color: isVerified
                        ? theme.palette.success.main
                        : "text.secondary",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                </Stack>

                {tag && (
                  <Chip
                    label={`Tag: ${tag}`}
                    size="small"
                    sx={{
                      mt: 0.5,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      height: 22,
                    }}
                  />
                )}

                <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                  <Rating
                    value={ratingValue}
                    readOnly
                    precision={0.5}
                    size="small"
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.warning.dark,
                    }}
                  >
                    {ratingValue.toFixed(1)} / 5.0
                  </Typography>
                </Stack>
              </Box>
            </Box>

            {/* Comment Block */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, textTransform: "uppercase", fontSize: "0.75rem" }}
              >
                Comment
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: theme.palette.grey[50],
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Icon
                  icon="solar:quote-up-bold"
                  width={24}
                  height={24}
                  style={{
                    color: alpha(theme.palette.primary.main, 0.25),
                    marginBottom: 8,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.primary",
                    lineHeight: 1.7,
                    fontStyle: "italic",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  "{commentText}"
                </Typography>
              </Box>
            </Box>

            {/* Metadata Footer */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: theme.palette.grey[50],
                fontSize: "0.85rem",
                color: "text.secondary",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Date Created:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formattedDate}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.success.main }}
                >
                  Published
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            textTransform: "none",
            fontWeight: 600,
            fontFamily,
            px: 3,
            "&:hover": {
              borderColor: theme.palette.primary.dark,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { TestimonialDetailModal };
